import type { Editor } from '@tiptap/react';
import clsx from 'clsx';
import { ImagePlusIcon } from 'lucide-react';
import { nanoid } from 'nanoid';
import React, { useCallback, useState } from 'react';
import { DropzoneOptions, useDropzone } from 'react-dropzone';

import { Button } from '@/ui/Button';
import { Icon, IconName } from '@/ui/Icon';
import { Input } from '@/ui/Input';
import { Label } from '@/ui/Label';
import { Popover, PopoverContent, PopoverTrigger } from '@/ui/Popover';
import { useTranslation } from 'react-i18next';

interface IAddImage {
	editor: Editor;
}

// TODO: ilia orlov - make dropzone zone larger to take up the entire container
// TODO: ilia orlov - add feature to handle multiple files at once
const AddImage = ({ editor }: IAddImage) => {
	const [fileName, setFileName] = useState('');
	const [link, setLink] = useState('');

	const isDisabled = !fileName && !link;

	const handleReset = useCallback(() => {
		setFileName('');
		setLink('');
	}, []);

	const handleConfirm = () => {
		editor
			.chain()
			.focus()
			.setImage({
				src: link,
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
				'data-fileId': nanoid(),
			})
			.run();

		handleReset();
	};

	const onDrop = useCallback((...[acceptedFiles]: Parameters<Required<DropzoneOptions>['onDrop']>) => {
		setLink(acceptedFiles[0] ? URL.createObjectURL(acceptedFiles[0]) : '');
		setFileName(acceptedFiles[0].name);
	}, []);

	const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

	if (!editor) return null;

	const { t } = useTranslation();

	return (
		<Popover modal>
			<PopoverTrigger asChild>
				<Button
					className={clsx('w-6 h-6 text-brand-gray-2 [&>svg]:size-6 [&>svg]:reset p-0 [&>svg]:left-[0px]', {
						'text-brand-blue': editor.isActive('link'),
					})}
					size="icon"
					icon="left"
					variant="tetriary"
				>
					<ImagePlusIcon />
				</Button>
			</PopoverTrigger>

			<PopoverContent className="p-0 w-96" align="center">
				<div className="flex flex-col gap-2 p-4">
					<div {...getRootProps()} className="flex flex-col gap-1 px-1 py-1 ">
						<Label htmlFor="image-url">{t('editor.addImage')}</Label>
						{/* TODO: ilia orlov - figure out better way to display input instead of copy-paste from Input component */}
						<div className="flex flex-col h-full rounded-lg border border-brand-gray-4 bg-white px-3 py-[10px] font-sans text-base leading-tight ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-brand-gray-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50">
							<Input {...getInputProps()} />
							{(isDragActive && <p>{t('editor.moveFiles')}...</p>) || (fileName && <p>{fileName}</p>) || (
								<p>{t('editor.imageDropzone')}</p>
							)}
						</div>
					</div>

					<div className="flex flex-wrap justify-between gap-2 px-1 py-1">
						<Button
							variant="danger"
							icon="left"
							size="sm"
							onClick={handleReset}
							title={t('editor.returnVal') as string}
							disabled={isDisabled}
						>
							<Icon name="trash" />
							{t('buttons.reset1')}
						</Button>

						<Button
							size="sm"
							variant="default"
							icon="left"
							onClick={handleConfirm}
							title={t('common.save') as string}
							disabled={isDisabled}
						>
							<Icon name={IconName.link} />
							{t('common.save')}
						</Button>
					</div>
				</div>
			</PopoverContent>
		</Popover>
	);
};

export { AddImage };
